/**
 * ChatComposerV2 — the V2 composer on Glaze, with the send↔Stop action
 * button (bead terra-design-v2-chat-yub.6, spec §1-3, §5-6).
 *
 * Markup/autogrow/submit-guard ported from the prototype
 * (`clayground-sc-terra-flow-panels/projects/sc-terra-flow/src/chat/ChatComposer.tsx`) — same
 * shell classes, same autogrow technique (Glaze's `Textarea` has no autogrow primitive), same
 * `GtmAgentComposerProps`-shaped prop subset so bead .15 (persona/skills affordances) can extend
 * this without a rewrite.
 *
 * Draft persistence is NOT ported from the prototype (that version used a bare `useState`). It is
 * ported from `chat-panel-core.tsx`'s `useComposerDraft` — same debounce/flush SEMANTICS (schedule
 * captures the target session at schedule time; a session switch or unmount flushes-then-cancels
 * the pending write; a sent message clears the draft synchronously) — but rebuilt on top of
 * `useDraftState(seed, seedKey)` instead of a bare `useState`, per `docs/v4-system/draft-state-
 * under-sse.md` and this epic's coding-standards.md ("No bare `useState` for the draft and no
 * reseed effect — `useDraftState` only"). `useDraftState`'s own render-phase reseed (keyed on
 * `sessionId`) is what makes an unrelated parent re-render (an `artifact:updated`-shaped SSE tick)
 * leave in-progress typing alone, and what makes switching `sessionId` display the new session's
 * stored draft.
 *
 * Library-sidebar mount point: this component renders ONLY the "Open toolkit" trigger button.
 * `LibrarySidebar` itself (and its click-outside backdrop) used to mount here too, but its
 * `absolute inset-y-0 right-8 w-[300px]` panel geometry assumes a FULL chat-panel-height
 * positioned ancestor — this composer's own box is short, so the panel rendered crammed into the
 * composer instead of as a full-height sidebar. `LibrarySidebar` now mounts at the chat panel
 * level (`FlowChatPanelV2.tsx`'s `ChatSessionViewV2`, matching V1's `chat-panel-core.tsx`
 * `KeystoneChatSurface` exactly). `librarySidebarOpen` lives ONLY there — this component exposes
 * `ChatComposerV2Handle` via `ref` so the panel can still route a sidebar pick into this
 * composer's own draft state without a second "is the library open" source of truth.
 */
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Textarea } from '@clay-run/glaze/textarea';
import { Badge } from '@clay-run/glaze/badge';
import { IconButton } from '@clay-run/glaze/iconbutton';
import { useDraftState } from '../../../../hooks/useDraftState';
import { detectTrigger } from '../../../skills/Composer';
import { CapabilityPopover } from '../../../skills/CapabilityPopover';
import { FLOW_ICONS } from '../../icons';
import type { PersonaListItem } from '../../../../types/personas';
import type { SkillListItem } from '../../../../types/skills';
import { ComposerActionButton } from './ComposerActionButton';
import { AUTOFILL_IGNORE_PROPS } from '../../../../lib/autofill';

/**
 * Trigger-span replace helpers — bead terra-design-v2-chat-yub.15, ported
 * (not imported) from `skills/Composer.tsx`'s private `stripTriggerToken`
 * (`:169-204`) and `pickSkill`'s inline slug-replace (`:170`ish) per that
 * bead's "Compose With" note ("port the logic, never the markup"; only
 * `detectTrigger` itself is an actual shared import — see this file's own
 * import above and that bead's "Do NOT Create" checklist).
 */
function stripTriggerToken(
  value: string,
  cursor: number,
  char: '@' | '/',
): { next: string; nextCursor: number } {
  const before = value.slice(0, cursor);
  const after = value.slice(cursor);
  const match = before.match(new RegExp(`(^|\\s)\\${char}[\\w-]*$`));
  if (!match) return { next: value, nextCursor: cursor };
  const tokenStart = match.index! + match[1].length;
  return { next: value.slice(0, tokenStart) + after, nextCursor: tokenStart };
}

function replaceSkillToken(
  value: string,
  cursor: number,
  slug: string,
): { next: string; nextCursor: number } {
  const before = value.slice(0, cursor);
  const after = value.slice(cursor);
  const match = before.match(/(^|\s)\/[\w-]*$/);
  if (!match) return { next: value, nextCursor: cursor };
  const tokenStart = match.index! + match[1].length;
  const insertion = `/${slug} `;
  return { next: value.slice(0, tokenStart) + insertion + after, nextCursor: tokenStart + insertion.length };
}

/** Matches `chat-panel-core.tsx`'s private `slugifySkillName` exactly (same
 *  fallback chain) — ported, not imported (that helper isn't exported). */
function slugifySkillName(s: SkillListItem): string {
  return (
    s.slug ||
    s.name
      .trim()
      .toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9-]/g, '')
      .replace(/-+/g, '-')
      .replace(/^-+|-+$/g, '') ||
    'skill'
  );
}

/** Library-sidebar skill insert: append `/slug ` to the end of whatever is
 *  already drafted — matches `chat-panel-core.tsx`'s
 *  `handlePickSkillFromSidebar` exactly (both surfaces). */
function insertSkillSlugAtEnd(value: string, slug: string): string {
  const trimmed = value.trimEnd();
  return trimmed.length === 0 ? `/${slug} ` : `${trimmed} /${slug} `;
}

/**
 * Imperative contract exposed to whichever ancestor now owns the library sidebar's `open` state
 * and mounts `LibrarySidebar` itself (the chat panel — see this file's header comment on the
 * mount-point fix). A pick made from the sidebar still has to land in THIS composer's own
 * `useSessionDraft`-backed `value`/`setValue` — that live state never left this component — so the
 * panel can't apply the pick itself; it calls back into these two methods instead. Both mirror the
 * composer's pre-fix local `handlePickPersonaFromSidebar`/`handlePickSkillFromSidebar` bodies
 * exactly, only swapping the local `setLibrarySidebarOpen(false)` for the `onCloseLibrary` prop.
 */
export interface ChatComposerV2Handle {
  pickPersonaFromSidebar: (p: PersonaListItem) => void;
  pickSkillFromSidebar: (s: SkillListItem) => void;
}

/**
 * Field height ceiling before the textarea scrolls internally instead of growing further — see
 * the prototype's own comment (`ChatComposer.tsx`): not from a design spec, picked so a pasted
 * paragraph can't push the composer past a comfortable fraction of the dock's height.
 */
const MAX_TEXTAREA_HEIGHT_PX = 160;

/** How long after the rep stops typing before the draft is persisted to the session store —
 *  matches `useComposerDraft`'s own constant (`chat-panel-core.tsx`). */
const DRAFT_PERSIST_DEBOUNCE_MS = 400;

export interface ChatComposerV2Props {
  /** The session this composer's draft is scoped to — the ONLY session knowledge this component
   *  holds, passed straight through to `useDraftState` as its `seedKey`. */
  sessionId: string;
  /** The session's stored draft (seed) — `entry.draftText` from `useFlowChatSessionController`. */
  draftText: string;
  /** Persist the composer's current text (debounced — see file header). */
  onDraftChange: (text: string) => void;
  onSubmit: (text: string) => void;
  onStop?: () => void;
  /** Whether THIS session's turn is in flight — swaps the send button to Stop
   *  (`ComposerActionButton`). */
  isStreaming: boolean;
  /** Carried through to `aria-busy` on the field wrapper; nothing here disables typing — see
   *  `ComposerActionButtonProps`'s own doc comment on why blocking input while busy would fight
   *  behavior the state layer already supports. */
  isBusy: boolean;
  /** Additional caller-supplied disable (e.g. no active thread to send into yet — the brief
   *  "Connecting…" window) — combined with the field's own empty-draft disable, never replacing
   *  it. */
  sendDisabled?: boolean;
  placeholder?: string;
  /**
   * A one-shot suggestion question to seed the draft with — `controller.pendingExample` from
   * `useFlowChatSessionController` (bead .7, "Add the V2 empty state with answerable suggestion
   * prompts"), set by `ChatEmptyStateV2`'s click handler. `null`/`undefined` when nothing is
   * pending. Overrides an existing draft, exactly like V1's `PreChatSurface initialValue`.
   */
  pendingExample?: string | null;
  /** Clears `pendingExample` once applied — `controller.consumePendingExample`. Required when
   *  `pendingExample` is ever non-null. */
  onConsumePendingExample?: () => void;

  // ---- Persona/skills affordances (bead terra-design-v2-chat-yub.15) ----

  /** `controller.personas` — surface-filtered list for the `@` picker and the
   *  library sidebar's Personas tab. Defaults to `[]` (no `@` affordance),
   *  keeping bead .6's existing callers/tests byte-compatible. */
  personas?: PersonaListItem[];
  /** `controller.skills` — surface-filtered list for the `/` picker and the
   *  library sidebar's Skills tab. Defaults to `[]`. */
  skills?: SkillListItem[];
  /**
   * The persona bound to this chat: the locally-picked pre-chat persona, or
   * (for an existing/reloaded conversation) a `makeBoundPersona`-shaped
   * sentinel built from the conversation's persisted `personaName` — see
   * `chat-panel-core.tsx`'s `composerPersona` derivation, which this prop
   * mirrors exactly. Non-null both gates the `@` picker off (regardless of
   * `started`) and renders the locked chip above the field. `null` (default)
   * renders no chip.
   */
  persona?: PersonaListItem | null;
  /**
   * True once the chat has started (an existing/committed conversation) —
   * gates the `@` picker off even while `persona` is still null (matches
   * `chat-panel-core.tsx`'s `started = messageCount > 0` semantics; a
   * committed conversation always carries ≥1 message by the time this
   * composer is mounted alongside it — see `FlowChatPanelV2.tsx`'s own
   * comment on this derivation). Defaults to `false` (pre-chat).
   */
  started?: boolean;
  /**
   * Fired when a persona is picked via `@` or the library sidebar's Personas
   * tab. The caller is responsible for making this a no-op once a persona is
   * already bound to an existing conversation — mirrors
   * `chat-panel-core.tsx`'s `handlePickPersona`/`handlePickPersonaFromSidebar`
   * no-ops for `KeystoneChatSurface`. Defaults to a no-op.
   */
  onPickPersona?: (p: PersonaListItem) => void;
  /** Opens the read-only DebugInspector drawer from the capability popover's
   *  footer link. Defaults to a no-op. */
  onOpenInspector?: () => void;

  // ---- Library sidebar mount point (mounting fix) ----

  /**
   * Opens the library sidebar. This composer renders ONLY the trigger button — the panel that
   * mounts this composer owns `librarySidebarOpen` and renders `LibrarySidebar` itself, at the
   * panel level, so the sidebar's `absolute inset-y-0`/rail geometry resolves against the full
   * chat-panel box instead of this composer's own short box. Defaults to a no-op (so a bare
   * prop-driven render in a test never throws clicking the trigger).
   */
  onOpenLibrary?: () => void;
  /**
   * Closes the library sidebar. Called by this composer's own persona/skill pick-from-sidebar
   * handlers (exposed via `ref` — see `ChatComposerV2Handle`) once a pick completes — the single
   * source of truth for "is the library open" lives in the panel, not here. Defaults to a no-op.
   */
  onCloseLibrary?: () => void;
}

/**
 * Session-scoped debounced draft, on top of `useDraftState`. See file header for why this exists
 * instead of reusing `chat-panel-core.tsx`'s `useComposerDraft` directly (that hook's OWN internal
 * state is a bare `useState`, the exact anti-pattern this epic forbids for V2 surfaces).
 *
 * Two flush paths, both required by the bead's Codex specs (§5, §6):
 *  - Unmount: the pending write (if any) is flushed to the CURRENT session before the component
 *    goes away (spec 5 — "Unmounting before 400ms persists the edit").
 *  - Session switch: detected in the render phase, BEFORE this render's incoming `sessionId`/
 *    `onDraftChange` overwrite the refs — snapshots the OUTGOING session's own callback + latest
 *    typed value into a ref (not a local variable — `useDraftState`'s own render-phase reseed can
 *    abort and re-invoke this component's render function once more in the same commit, and only
 *    the LAST invocation's local variables survive to see its effects run; a ref written on the
 *    first pass persists into the retry) so the layout effect below flushes to the right target
 *    regardless of which pass ends up committing (spec 6 — "flushes the latest Acme text to
 *    Acme" when switching to Globex).
 */
function useSessionDraft(
  sessionId: string,
  seed: string,
  onDraftChange: (text: string) => void,
  pendingExample: string | null,
  onConsumePendingExample: (() => void) | undefined,
): { value: string; setValue: (next: string) => void; clearSent: () => void } {
  const { draft, setDraft, reseed } = useDraftState<string>(seed, sessionId);
  const value = draft ?? '';

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const valueRef = useRef(value);
  const onDraftChangeRef = useRef(onDraftChange);
  const sessionIdRef = useRef(sessionId);
  const outgoingFlushRef = useRef<(() => void) | null>(null);

  if (sessionIdRef.current !== sessionId) {
    if (timerRef.current !== null) {
      const outgoingOnDraftChange = onDraftChangeRef.current;
      const outgoingValue = valueRef.current;
      outgoingFlushRef.current = () => outgoingOnDraftChange(outgoingValue);
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    sessionIdRef.current = sessionId;
  }

  // Safe to advance to the incoming session's live props now — any pending write for the
  // OUTGOING session was already snapshotted above.
  valueRef.current = value;
  onDraftChangeRef.current = onDraftChange;

  useLayoutEffect(() => {
    if (!outgoingFlushRef.current) return;
    const flush = outgoingFlushRef.current;
    outgoingFlushRef.current = null;
    flush();
  });

  // Unmount-only flush (mount-once — real unmount, not a session switch, which the effect above
  // already handles).
  useEffect(() => {
    return () => {
      if (timerRef.current === null) return;
      clearTimeout(timerRef.current);
      timerRef.current = null;
      onDraftChangeRef.current(valueRef.current);
    };
  }, []);

  const setValue = useCallback(
    (next: string) => {
      valueRef.current = next;
      setDraft(next);
      if (timerRef.current !== null) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        onDraftChangeRef.current(valueRef.current);
      }, DRAFT_PERSIST_DEBOUNCE_MS);
    },
    [setDraft],
  );

  // Clears the draft SYNCHRONOUSLY (bypassing the debounce) so a stale draft never reappears
  // after the message it was drafting has actually been sent — `useComposerDraft`'s `clearSent`.
  const clearSent = useCallback(() => {
    valueRef.current = '';
    setDraft('');
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    onDraftChangeRef.current('');
  }, [setDraft]);

  // Suggestion-seed path (bead .7): `reseed` (not `setDraft`) so the applied text is also the new
  // pristine baseline, not a `setDraft` merge — and so StrictMode's setup→cleanup→setup double-
  // invoke of the effect below is a true no-op on its second call (`reseed(x)` twice is identical
  // to once). The persisted-draft write still goes through the SAME 400ms debounce/session-switch-
  // flush plumbing `setValue` uses (coding-standards.md: "no second draft-seeding path").
  const seedFromExample = useCallback(
    (next: string) => {
      valueRef.current = next;
      reseed(next);
      if (timerRef.current !== null) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        onDraftChangeRef.current(valueRef.current);
      }, DRAFT_PERSIST_DEBOUNCE_MS);
    },
    [reseed],
  );

  // Applies `pendingExample` (an example question just clicked in `ChatEmptyStateV2`) and
  // consumes the parent's one-shot — mirrors V1's `PreChatSurface` effect
  // (`chat-panel-core.tsx`: "Seed the composer once when an example question is clicked, then
  // clear the parent's one-shot"). Depends on `pendingExample` itself (not mount-only) so a
  // SECOND suggestion clicked before the first is sent re-seeds again, exactly as V1 does.
  const onConsumePendingExampleRef = useRef(onConsumePendingExample);
  onConsumePendingExampleRef.current = onConsumePendingExample;
  useEffect(() => {
    if (pendingExample === null || pendingExample === undefined) return;
    seedFromExample(pendingExample);
    onConsumePendingExampleRef.current?.();
  }, [pendingExample, seedFromExample]);

  return { value, setValue, clearSent };
}

export const ChatComposerV2 = forwardRef<ChatComposerV2Handle, ChatComposerV2Props>(function ChatComposerV2(
  {
    sessionId,
    draftText,
    onDraftChange,
    onSubmit,
    onStop,
    isStreaming,
    isBusy,
    sendDisabled,
    placeholder = 'Ask about this account…',
    pendingExample,
    onConsumePendingExample,
    personas = [],
    skills = [],
    persona = null,
    started = false,
    onPickPersona = () => {},
    onOpenInspector = () => {},
    onOpenLibrary = () => {},
    onCloseLibrary = () => {},
  }: ChatComposerV2Props,
  ref,
) {
  const { value, setValue, clearSent } = useSessionDraft(
    sessionId,
    draftText,
    onDraftChange,
    pendingExample ?? null,
    onConsumePendingExample,
  );
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Autogrow: reset height then measure `scrollHeight` — see `MAX_TEXTAREA_HEIGHT_PX`'s doc
  // comment for why this can't use a Glaze primitive instead.
  useLayoutEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, MAX_TEXTAREA_HEIGHT_PX)}px`;
  }, [value]);

  const trimmed = value.trim();
  const isEmpty = trimmed.length === 0;
  const composedSendDisabled = isEmpty || Boolean(sendDisabled);

  // ---- Persona/skills trigger detection (bead .15) --------------------------
  // `cursor` mirrors V1's `Composer` own tracking: updated from the native
  // `selectionStart` on every change/select/click/keyup, then synced back
  // onto the element via the layout effect below (so a programmatic pick —
  // which moves `cursor` without a native selection event — still moves the
  // visible caret).
  const [cursor, setCursor] = useState(value.length);

  useLayoutEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    if (el.selectionStart !== cursor) {
      try {
        el.setSelectionRange(cursor, cursor);
      } catch {
        // Detached input — ignore.
      }
    }
  }, [cursor, value]);

  const personaTrigger = detectTrigger(value, cursor, '@');
  const skillTrigger = detectTrigger(value, cursor, '/');

  // `@` is only live pre-chat, and only while no persona is bound yet —
  // identical gate to `chat-panel-core.tsx`'s `Composer` usage
  // (`!started && persona === null`).
  const personaPopupActive = personaTrigger !== null && !started && persona === null;
  const skillPopupActive = skillTrigger !== null;

  const filteredPersonas = useMemo(() => {
    if (!personaPopupActive) return [];
    const q = (personaTrigger ?? '').toLowerCase();
    return personas.filter(
      (p) => !q || p.name.toLowerCase().includes(q) || p.description.toLowerCase().includes(q),
    );
  }, [personas, personaTrigger, personaPopupActive]);

  const filteredSkills = useMemo(() => {
    if (!skillPopupActive) return [];
    const q = (skillTrigger ?? '').toLowerCase();
    return skills.filter(
      (s) => !q || s.name.toLowerCase().includes(q) || s.description.toLowerCase().includes(q),
    );
  }, [skills, skillTrigger, skillPopupActive]);

  // Personas take precedence over skills when (implausibly) both regexes
  // somehow matched — same precedence `chat-panel-core.tsx`'s `Composer`
  // uses. A popup with zero filtered results renders nothing (spec 4 —
  // "exposes no selectable results").
  const popup: 'personas' | 'skills' | null =
    personaPopupActive && filteredPersonas.length > 0
      ? 'personas'
      : skillPopupActive && filteredSkills.length > 0
        ? 'skills'
        : null;

  const pickPersonaFromTrigger = useCallback(
    (p: PersonaListItem) => {
      onPickPersona(p);
      const { next, nextCursor } = stripTriggerToken(value, cursor, '@');
      setValue(next);
      setCursor(nextCursor);
    },
    [onPickPersona, value, cursor, setValue],
  );

  const pickSkillFromTrigger = useCallback(
    (s: SkillListItem) => {
      const slug = slugifySkillName(s);
      const { next, nextCursor } = replaceSkillToken(value, cursor, slug);
      setValue(next);
      setCursor(nextCursor);
    },
    [value, cursor, setValue],
  );

  const handlePickPersonaFromSidebar = useCallback(
    (p: PersonaListItem) => {
      onPickPersona(p);
      onCloseLibrary();
    },
    [onPickPersona, onCloseLibrary],
  );

  const handlePickSkillFromSidebar = useCallback(
    (s: SkillListItem) => {
      const slug = slugifySkillName(s);
      setValue(insertSkillSlugAtEnd(value, slug));
      onCloseLibrary();
    },
    [value, setValue, onCloseLibrary],
  );

  // Imperative contract for whichever ancestor now mounts `LibrarySidebar` (see
  // `ChatComposerV2Handle`'s doc comment) — a pick made from the sidebar still has to land in
  // THIS composer's own draft state, so the panel calls back into these two methods rather than
  // applying the pick itself.
  useImperativeHandle(
    ref,
    () => ({
      pickPersonaFromSidebar: handlePickPersonaFromSidebar,
      pickSkillFromSidebar: handlePickSkillFromSidebar,
    }),
    [handlePickPersonaFromSidebar, handlePickSkillFromSidebar],
  );

  // One guarded submit path — Enter and the action button both call this. `submittingRef` blocks
  // two submissions dispatched before React has re-rendered with the cleared draft (Codex Phase 4b
  // "One guarded submit path" finding); ordinary re-render-separated double-sends are already
  // blocked by `isEmpty` once the first send clears the draft.
  const submittingRef = useRef(false);
  const submit = useCallback(() => {
    if (isStreaming || isBusy || composedSendDisabled || submittingRef.current) return;
    submittingRef.current = true;
    onSubmit(trimmed);
    clearSent();
    queueMicrotask(() => {
      submittingRef.current = false;
    });
  }, [isStreaming, isBusy, composedSendDisabled, onSubmit, trimmed, clearSent]);

  const updateCursorFromEvent = (e: { currentTarget: HTMLTextAreaElement }) => {
    setCursor(e.currentTarget.selectionStart ?? e.currentTarget.value.length);
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setValue(e.target.value);
    setCursor(e.target.selectionStart ?? e.target.value.length);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      // A pick list is open — Enter selects its first (best) match instead
      // of submitting. No arrow-key activeIdx tracking in V2 yet (not in
      // this bead's Codex specs); mouse/click selection is the primary path.
      if (popup === 'personas' && filteredPersonas.length > 0) {
        e.preventDefault();
        pickPersonaFromTrigger(filteredPersonas[0]);
        return;
      }
      if (popup === 'skills' && filteredSkills.length > 0) {
        e.preventDefault();
        pickSkillFromTrigger(filteredSkills[0]);
        return;
      }
    }
    if (e.key !== 'Enter' || e.shiftKey) return;
    // IME candidate confirmation — Shift+Enter's sibling case for "don't accidentally send"
    // (spec 2). Let the IME handle this Enter; no submit, no preventDefault.
    if (e.nativeEvent.isComposing) return;
    e.preventDefault();
    submit();
  };

  return (
    <div
      data-testid="chat-composer-v2"
      aria-busy={isBusy || undefined}
      className="relative mx-3 mb-3 rounded-xl border-[0.5px] border-border-primary bg-bg-primary shadow-sm focus-within:border-content-action"
    >
      {persona && (
        <div className="flex items-center gap-2 px-3 pt-2.5" data-testid="locked-persona-chip">
          <Badge size="sm" color="purple">
            <span aria-hidden className="mr-1">
              {persona.icon ?? '🪄'}
            </span>
            {persona.name}
          </Badge>
        </div>
      )}

      {popup === 'personas' && (
        <div
          role="listbox"
          aria-label="Personas"
          className="absolute bottom-full left-3 right-3 z-20 mb-2 overflow-hidden rounded-lg border border-border-primary bg-bg-primary shadow-lg"
        >
          {filteredPersonas.map((p) => (
            <div
              key={p.id}
              role="option"
              aria-selected={false}
              onClick={() => pickPersonaFromTrigger(p)}
              className="flex cursor-pointer items-center gap-2 px-3 py-2 hover:bg-bg-secondary"
            >
              <span aria-hidden>{p.icon ?? '🪄'}</span>
              <span className="text-sm font-medium text-content-primary">{p.name}</span>
            </div>
          ))}
        </div>
      )}

      {popup === 'skills' && (
        <div
          role="listbox"
          aria-label="Skills"
          className="absolute bottom-full left-3 right-3 z-20 mb-2 overflow-hidden rounded-lg border border-border-primary bg-bg-primary shadow-lg"
        >
          {filteredSkills.map((s) => (
            <div
              key={s.id}
              role="option"
              aria-selected={false}
              onClick={() => pickSkillFromTrigger(s)}
              className="flex cursor-pointer items-center gap-2 px-3 py-2 hover:bg-bg-secondary"
            >
              <span aria-hidden>{s.icon ?? '⚡'}</span>
              <span className="text-sm font-medium text-content-primary">{s.name}</span>
            </div>
          ))}
        </div>
      )}

      <Textarea
        {...AUTOFILL_IGNORE_PROPS}
        ref={textareaRef}
        borderless
        resizable={false}
        rows={1}
        aria-label="Message input"
        placeholder={placeholder}
        value={value}
        className="max-h-40 overflow-y-auto"
        onChange={handleChange}
        onSelect={updateCursorFromEvent}
        onClick={updateCursorFromEvent}
        onKeyUp={updateCursorFromEvent}
        onKeyDown={handleKeyDown}
      />
      <div className="flex items-center justify-between px-[calc(--spacing(3.5)-1px)] pb-2 sm:px-[calc(--spacing(3)-1px)]">
        <div className="flex items-center gap-1.5">
          <CapabilityPopover onOpenInspector={onOpenInspector} align="left" />
          <IconButton
            icon={FLOW_ICONS.library}
            aria-label="Open toolkit"
            variant="ghost"
            size="sm"
            onClick={onOpenLibrary}
          />
        </div>
        <ComposerActionButton
          compact={false}
          isStreaming={isStreaming}
          // The composer's own contract is "the button IS the Stop control while running" — no
          // caller in this epic needs a send-while-streaming affordance (brief §9 #7 is deferred).
          canSubmitWhileStreaming={false}
          isSubmitting={false}
          sendDisabled={composedSendDisabled}
          onSubmit={submit}
          onStop={onStop}
        />
      </div>
    </div>
  );
});
